import {
  TEditingEntity,
  TEntityLink,
  TLinkSource,
  TUnlinkedItem
} from "components/feature-entity-linking-page/models";
import { useCallback, useMemo } from "react";
import { getLinkType } from "components/feature-entity-linking-page/utils";
import { FeatureTypes } from "entity-app/constants";
import FeatureRowActivityCard from "./feature-row-activity-card";
import FeatureRowSubmittalCard from "./feature-row-submittal-card";
import FeatureRowMaterialCard from "./feature-row-material-card";

function FeatureRowCard({
  item,
  isEditing,
  editingEntity,
  setEditingEntity,
  isAllowSetting,
  featureTypeId,
  selectedEntityInfo,
  hidePredecessorTag = false
}: {
  item: TLinkSource;
  isEditing: boolean;
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  isAllowSetting: boolean;
  featureTypeId: FeatureTypes;
  selectedEntityInfo: TEntityLink;
  hidePredecessorTag?: boolean;
}) {
  const onRemoveRow = useCallback(
    (rowId: string) => {
      setEditingEntity((pre) => {
        if (!pre) return pre;

        // Find the item being removed
        const removedItem = pre.list.find((x) => x.featureInstanceId === rowId);
        if (!removedItem) return pre;

        // Only track this explicitly unlinked item (NOT nested activities)
        // Nested activities are only tracked when explicitly unlinked via onRemoveSubActivityRow
        const newUnlinkedItems: TUnlinkedItem[] = [];

        // Add the removed item to unlinked buffer (only if not already there)
        const alreadyUnlinked = pre.unlinkedItems.some(
          (u) =>
            u.item.featureInstanceId === rowId &&
            u.parentMaterialId === undefined
        );
        if (!alreadyUnlinked) {
          newUnlinkedItems.push({ item: removedItem });
        }

        return {
          ...pre,
          list: pre.list.filter((x) => x.featureInstanceId !== rowId),
          unlinkedItems: [...pre.unlinkedItems, ...newUnlinkedItems]
        };
      });
    },
    [setEditingEntity]
  );

  const onOffsetChange = useCallback(
    ({
      offset,
      activityLink
    }: {
      offset: number | null;
      activityLink: TLinkSource;
    }) => {
      const activityLinkedItem = structuredClone(activityLink);
      activityLinkedItem.offset_duration = offset;

      setEditingEntity((pre) => {
        if (!pre) return pre;

        const indexActivity = pre.list.findIndex(
          (x) => x.featureInstanceId === activityLink.featureInstanceId
        );
        return {
          ...pre,
          list: [
            ...pre.list.slice(0, indexActivity),
            activityLinkedItem,
            ...pre.list.slice(indexActivity + 1)
          ]
        } as TEditingEntity;
      });
    },
    [setEditingEntity]
  );

  const updateReverseLinking = useCallback(
    ({ activityLink }: { activityLink: TLinkSource }) => {
      const itemActivity = structuredClone(activityLink);
      itemActivity.reverse_dependency = !activityLink.reverse_dependency;
      itemActivity.link_type = getLinkType(
        itemActivity.reverse_dependency,
        itemActivity.isLinkedWithStartdate
      );

      if (itemActivity.reverse_dependency) {
        itemActivity.offset_duration = null;
      }

      setEditingEntity((pre) => {
        if (!pre) return pre;

        const indexActivity = pre.list.findIndex(
          (x) => x.featureInstanceId === itemActivity.featureInstanceId
        );

        if (indexActivity > -1) {
          return {
            ...pre,
            list: [
              ...pre.list.slice(0, indexActivity),
              itemActivity,
              ...pre.list.slice(indexActivity + 1)
            ]
          } as TEditingEntity;
        }
        return pre;
      });
    },
    [setEditingEntity]
  );

  const card = useMemo(() => {
    const featureId = item.feature_instance?.feature.id;
    switch (featureId) {
      case 9:
        return (
          <FeatureRowActivityCard
            item={item}
            isEditing={isEditing}
            onRemoveRow={onRemoveRow}
            isAllowSetting={isAllowSetting}
            onOffsetChange={onOffsetChange}
            updateReverseLinking={updateReverseLinking}
          />
        );
      case 1:
        return (
          <FeatureRowSubmittalCard
            item={item}
            isEditing={isEditing}
            onRemoveRow={onRemoveRow}
          />
        );
      case 2:
        return (
          <FeatureRowMaterialCard
            materialLink={item}
            isEditing={isEditing}
            onRemoveRow={onRemoveRow}
            editingEntity={editingEntity}
            setEditingEntity={setEditingEntity}
            featureTypeId={featureTypeId}
            selectedEntityInfo={selectedEntityInfo}
            hidePredecessorTag={hidePredecessorTag}
          />
        );
      default:
        return <div>Unknown Feature Type {featureId} </div>;
    }
  }, [
    editingEntity,
    isAllowSetting,
    isEditing,
    item,
    onOffsetChange,
    onRemoveRow,
    setEditingEntity,
    updateReverseLinking,
    featureTypeId,
    selectedEntityInfo,
    hidePredecessorTag
  ]);

  return card;
}
export default FeatureRowCard;
